import { ArticleStatus, IArticle, IQuantityPerUnit } from '@alberta/konexi-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { SwodocNodeArticleDataSingleOrderService } from './swodoc-node-article-data-single-order.service';
export interface SwodocArticle extends IArticle {
  articleId: string;
  packagingUnit: IQuantityPerUnit;
  quantity: number;
  productGroupName: string;
  annotation?: string;
  isChecked?: boolean;
}
@Component({
  selector: 'swodoc-node-article-data-single-order',
  templateUrl: 'swodoc-node-article-data-single-order.html',
  styleUrls: ['swodoc-node-article-data-single-order.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwodocNodeArticleDataSingleOrder implements OnInit {
  @Input() node: any;

  // All articles including unchecked ones
  private _allArticles: SwodocArticle[] = [];

  get TemplateNodeTitle() {
    return this.node?.title as string;
  }

  // This will return all articles (both checked and unchecked)
  get articles(): SwodocArticle[] {
    return this._allArticles;
  }

  // This will update only the internal array
  set articles(value: SwodocArticle[]) {
    this._allArticles = value;
  }

  // This will return only checked articles to be saved in responses
  private get checkedArticles(): SwodocArticle[] {
    return this._allArticles.filter(article => article.isChecked !== false);
  }

  // This will update the responses with only checked articles
  private updateResponsesValue(): void {
    if (this.node?.responses?.[0]) {
      // Create a clean copy of checked articles without the isChecked property
      const cleanedArticles = this.checkedArticles.map(article => {
        // Create a new object without the isChecked property
        const { isChecked, ...cleanArticle } = article;
        return cleanArticle;
      });

      this.node.responses[0].value = cleanedArticles;
    }
  }

  InfoBoxType = InfoBoxType;
  restriction = article => article.status != ArticleStatus.Available;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _articleCheckboxStateService: SwodocNodeArticleDataSingleOrderService
  ) {}

  ngOnInit(): void {
    // Check if we have stored state for this node
    // The service holds the state of the checked articles, so we can restore it after page rendering
    // We dont save the isChecked state down to the audit. There only the finally checked articles are saved
    if (this.node?.id && this._articleCheckboxStateService.hasArticleState(this.node.id)) {
      // Restore state from service
      this._allArticles = this._articleCheckboxStateService.getArticleState(this.node.id) || [];
      // Make sure responses are updated with the restored state
      this.updateResponsesValue();
    } else {
      // Initialize all articles from responses and mark them as checked by default
      if (this.node?.responses?.[0]?.value) {
        this._allArticles = this.node.responses[0].value.map(article => ({
          ...article,
          isChecked: true,
        }));

        // Save initial state to service
        if (this.node?.id) {
          this._articleCheckboxStateService.saveArticleState(this.node.id, this._allArticles);
        }
      }
    }
  }

  public handleCheckBoxChanged(checked: boolean, articleId: string) {
    // Find the article in our internal array
    const article = this._allArticles.find(item => item._id === articleId);
    if (article) {
      // Update the checked state
      article.isChecked = checked;

      // Update the responses value in the audit with only checked articles
      this.updateResponsesValue();

      // Save the current state to the service
      if (this.node?.id) {
        this._articleCheckboxStateService.saveArticleState(this.node.id, this._allArticles);
      }

      // Trigger change detection
      this._changeDetectorRef.markForCheck();
    }
  }

  public handleAnnotationChange(annotation: string, articleId: string) {
    const changedArticle = this.articles.find((item: IArticle) => item._id === articleId);
    if (changedArticle) {
      changedArticle.annotation = annotation;
      if (changedArticle.isChecked !== false) {
        // Update the responses value with only checked articles
        this.updateResponsesValue();
      }

      // Save the current state to the service
      if (this.node?.id) {
        this._articleCheckboxStateService.saveArticleState(this.node.id, this._allArticles);
      }

      this._changeDetectorRef.markForCheck();
    }
  }
}
